import { Column, Index, JoinColumn, ManyToOne } from 'typeorm';
import { Entity } from '../../decorators';
import { BaseEntity } from './base.entity';
import { MerchandiserEntity } from './merchandiser.entity';
import { SensorDeviceEntity } from './sensor-device.entity';
import { SensorEntity } from './sensor.entity';

/**
 * PD-15417 — one alert "excursion": a continuous stretch during which a
 * sensor-equipped merchandiser was out of spec. Two kinds:
 *
 *  - TEMPERATURE — one or more of a merchandiser's zones sustained past the
 *    configured threshold. One alert per merchandiser since PD-16196; the
 *    modules behind it are in `outOfRangeSensorIds`.
 *  - OFFLINE — a previously-reporting device stopped transmitting (per
 *    device; `fkSensorDeviceId` set). Silence usually precedes the melt: a
 *    dead unit sends no warm readings, it just goes quiet.
 *
 * The open row (endedAt null) is one problem. Recovery closes the row
 * (endedAt set) — no manual acknowledgment is required. PD-16196: emails are
 * consolidated per health-cron run; `notifyCount` / `lastNotifiedAt` record
 * the email steps sent for this alert (1 = first reported, 2 = four-hour
 * temperature reminder). `snoozedUntil` leaves a known issue awaiting service
 * out of emails; the in-app badge stays visible either way.
 *
 * Only merchandisers assigned to a client location alert, so
 * `fkMerchandiserId` is always set (denormalized for badge queries).
 */
export enum SensorAlertType {
	TEMPERATURE = 'temperature',
	OFFLINE = 'offline',
	/**
	 * PD-15482 — a unit that was placed at a customer and has since reported
	 * from somewhere else. Usually a theft; occasionally a relocation nobody
	 * recorded.
	 */
	MOVED = 'moved',
	/**
	 * PD-15482 — assigned to a customer but never seen there. Distinct from
	 * MOVED and from OFFLINE: nothing is wrong with the unit, it just has not
	 * been delivered.
	 */
	NOT_DELIVERED = 'not_delivered',
	/**
	 * PD-15733 — a module that is present but not measuring: the head unit
	 * keeps reporting on schedule while this module answers every poll with
	 * the "not responding" sentinel (loose cable, failed module). Distinct
	 * from OFFLINE, where the whole unit goes silent. Opens after several
	 * consecutive sentinel readings on a mapped sensor of a client-placed
	 * merchandiser; the first real measurement closes it.
	 */
	SENSOR_FAULT = 'sensor_fault',
	/**
	 * PD-15780 — a unit that was provisioned/linked but has NEVER reported.
	 * The offline evaluation cannot see these (it requires a lastSeen), yet
	 * this is exactly what a wrong server URL / API key / dead SIM looks
	 * like from our side. Opens after a grace period from device creation;
	 * the first successful check-in closes it.
	 */
	NEVER_REPORTED = 'never_reported',
	/**
	 * PD-15780 — the unit reports, but far from its expected cadence (fleet
	 * spec 600s): a missed interval write leaves a unit at e.g. 7200s and it
	 * just looks like sparse data. Measured from actual submission gaps, so
	 * no firmware cooperation is needed. Closes when the cadence conforms.
	 */
	CADENCE_OUT_OF_SPEC = 'cadence_out_of_spec',
}

/**
 * PD-16061 — why a SENSOR_FAULT alert is open. One open alert per
 * (sensor, reason): a module can be blind on depth AND silent on
 * temperature at the same time, and each condition clears on its own.
 * Stored as a plain string column (not a MySQL enum) so a future reason is
 * an additive change database sync can carry without an ALTER.
 */
export enum SensorFaultReason {
	/**
	 * The module answers every poll with the "not responding" sentinel (or
	 * no distance at all) — the original PD-15733 fault. Loose cable or a
	 * failed module.
	 */
	NO_DEPTH = 'no_depth',
	/**
	 * The module reports, but no reading in the last day carried a module
	 * temperature. The zone's temperature is unmonitored even though fill
	 * may still be measured.
	 */
	NO_TEMPERATURE = 'no_temperature',
	/**
	 * Days of readings inside the level-agreement tolerance while the chest
	 * is not full — product is being sold but the depth never moves, so the
	 * laser is looking at something other than the product.
	 */
	DEPTH_STUCK = 'depth_stuck',
	/** Consecutive readings of exactly 0 mm — a laser cannot measure that. */
	DEPTH_ZERO = 'depth_zero',
	/**
	 * Consecutive readings further than the empty floor allows — the module
	 * is measuring past the bin (mis-mounted, mis-calibrated, or faulted).
	 */
	DEPTH_OUT_OF_RANGE = 'depth_out_of_range',
}

@Index('sensor_alert_merchandiser_open_idx', ['merchandiserId', 'endedAt'])
@Index('sensor_alert_sensor_idx', ['sensorId'])
@Index('sensor_alert_device_idx', ['sensorDeviceId'])
@Entity('sensor_alert')
export class SensorAlertEntity extends BaseEntity {
	@Column('enum', { name: 'alertType', enum: SensorAlertType })
	public alertType!: SensorAlertType;

	@Column('int', { name: 'fk_merchandiser__id', unsigned: true })
	public merchandiserId!: number;

	/**
	 * The module a per-module alert is about (SENSOR_FAULT). Null on
	 * TEMPERATURE alerts since PD-16196, which are per merchandiser; the modules
	 * behind one are in `outOfRangeSensorIds`. Older TEMPERATURE rows carry the
	 * module id.
	 */
	@Column('int', { name: 'fk_sensor__id', unsigned: true, nullable: true })
	public sensorId!: number | null;

	/** Set for OFFLINE alerts — the device that went silent. */
	@Column('int', {
		name: 'fk_sensor_device__id',
		unsigned: true,
		nullable: true,
	})
	public sensorDeviceId!: number | null;

	@Column('datetime', { name: 'startedAt' })
	public startedAt!: Date;

	/** Null while the excursion is open; set when the condition recovers. */
	@Column('datetime', { name: 'endedAt', nullable: true })
	public endedAt!: Date | null;

	/** TEMPERATURE only — the worst reading seen during the excursion (°F). */
	@Column('int', { name: 'peakTemperatureF', nullable: true })
	public peakTemperatureF!: number | null;

	/**
	 * TEMPERATURE only (PD-16196) — the merchandiser's modules currently out of
	 * range, refreshed on every evaluation while the alert is open. One alert
	 * covers the whole merchandiser, so this is how every surface names the
	 * module or modules behind it.
	 */
	@Column('json', { name: 'outOfRangeSensorIds', nullable: true })
	public outOfRangeSensorIds!: Array<number> | null;

	/**
	 * SENSOR_FAULT only (PD-16061) — which fault rule opened this alert. Null
	 * on any other type. Rows opened before this column existed are
	 * backfilled to `no_depth` (the only rule that existed then).
	 */
	@Column('varchar', { name: 'faultReason', length: 32, nullable: true })
	public faultReason!: SensorFaultReason | null;

	@Column('datetime', { name: 'lastNotifiedAt', nullable: true })
	public lastNotifiedAt!: Date | null;

	@Column('int', { name: 'notifyCount', default: 0 })
	public notifyCount!: number;

	/** Left out of sensor alert emails until this time (known issue awaiting service). */
	@Column('datetime', { name: 'snoozedUntil', nullable: true })
	public snoozedUntil!: Date | null;

	@ManyToOne(() => MerchandiserEntity)
	@JoinColumn({ name: 'fk_merchandiser__id' })
	public fkMerchandiser!: MerchandiserEntity;

	@ManyToOne(() => SensorEntity)
	@JoinColumn({ name: 'fk_sensor__id' })
	public fkSensor!: SensorEntity | null;

	@ManyToOne(() => SensorDeviceEntity)
	@JoinColumn({ name: 'fk_sensor_device__id' })
	public fkSensorDevice!: SensorDeviceEntity | null;
}
